import React from 'react';
import { useTranslation } from 'react-i18next';
import EditableText from './EditableText';
import NFPADiamond from './NFPADiamond';
import { GHSIconRenderer } from './GHSIcons';
import ModonLogo from './ModonLogo';
import { smartMatchChemical, applyTemplateToChemical } from '../data/chemicalDatabase';
import {
  ClipboardCheck,
  HardHat,
  Warehouse,
  Briefcase,
  Droplets,
  UserCog,
  Siren,
  ListChecks,
  Users,
  Calendar,
  FileCheck2,
  X,
  Plus,
  PhoneCall,
} from 'lucide-react';

const SectionBadge = ({ number }) => (
  <div className="inline-flex items-center justify-center bg-[#1a4d80] text-white font-bold text-sm w-7 h-7 rounded">
    {number}
  </div>
);

const SectionHeader = ({ number, title, onTitleChange }) => (
  <div className="flex items-center gap-2 mb-2">
    <SectionBadge number={number} />
    <EditableText
      value={title}
      onChange={onTitleChange}
      className="text-[#1a4d80] font-bold text-base"
    />
    <div className="flex-1 h-px bg-slate-300 ml-2" />
  </div>
);

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2 text-[13px] leading-6 text-slate-800">
      <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[#1a4d80] flex-shrink-0" />
      <span className="flex-1">{children}</span>
    </li>
  );
}

// Risk badge color — recognizes both Arabic and English risk class labels.
function risksColorClass(level) {
  const v = (level || '').trim().toLowerCase();
  if (v.includes('منخفض') || v === 'low') return 'bg-emerald-600 text-white';
  if (v.includes('متوسط') || v === 'medium') return 'bg-amber-500 text-white';
  if (v.includes('مرتفع') || v.includes('عالي') || v === 'high') return 'bg-red-600 text-white';
  return 'bg-slate-300 text-slate-800';
}

export default function SDSTemplate({ data, setData }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const dir = isEn ? 'ltr' : 'rtl';

  // Build a cross-language localization map automatically from every key in the
  // `sds.defaults.*`, `sds.nfpa.*` and `sds.headerDefaults.*` namespaces.
  // For each key we register BOTH the Arabic and English value as lookup keys
  // pointing back to the key — so a stored value in either language is mapped
  // to the current-language translation.
  const localizationMap = React.useMemo(() => {
    const out = {};
    const namespaces = ['sds.defaults', 'sds.nfpa', 'sds.headerDefaults'];
    // Walk the ar/en translation trees for the chosen namespaces.
    const arRes = i18n.getResourceBundle('ar', 'translation') || {};
    const enRes = i18n.getResourceBundle('en', 'translation') || {};
    const pick = (obj, path) => path.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
    for (const ns of namespaces) {
      const arNode = pick(arRes, ns);
      const enNode = pick(enRes, ns);
      if (!arNode || typeof arNode !== 'object') continue;
      for (const k of Object.keys(arNode)) {
        const arVal = arNode[k];
        const enVal = enNode?.[k];
        if (typeof arVal === 'string' && typeof enVal === 'string') {
          const fullKey = `${ns}.${k}`;
          out[arVal.trim()] = fullKey;
          out[enVal.trim()] = fullKey;
        }
      }
    }
    return out;
  }, [i18n.language]);

  // L() returns the localized version IF the stored value matches a known default.
  // Otherwise the user's customized text is returned untouched.
  const L = (val) => {
    if (typeof val !== 'string') return val;
    const trimmed = val.trim();
    const key = localizationMap[trimmed];
    return key ? t(key) : val;
  };

  const update = (path, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updateChemical = (id, field, value) => {
    setData((prev) => ({
      ...prev,
      chemicals: prev.chemicals.map((c) => {
        if (c.id !== id) return c;
        let updated = { ...c, [field]: value };
        // If the name changed, sync risk.material AND try smart auto-fill from DB
        if (field === 'name') {
          updated.risk = { ...updated.risk, material: value };
          const tpl = smartMatchChemical(value);
          if (tpl) {
            updated = applyTemplateToChemical(updated, tpl);
            // Preserve the user's exact spelling/casing of the typed name
            updated.name = value;
            updated.risk.material = value;
          }
        }
        return updated;
      }),
    }));
  };

  const updateChemicalNFPA = (id, field, value) => {
    setData((prev) => ({
      ...prev,
      chemicals: prev.chemicals.map((c) =>
        c.id === id ? { ...c, nfpa: { ...c.nfpa, [field]: value } } : c
      ),
    }));
  };

  const updateChemicalRisk = (id, field, value) => {
    setData((prev) => ({
      ...prev,
      chemicals: prev.chemicals.map((c) =>
        c.id === id ? { ...c, risk: { ...c.risk, [field]: value } } : c
      ),
    }));
  };

  const removeChemical = (id) => {
    setData((prev) => ({ ...prev, chemicals: prev.chemicals.filter((c) => c.id !== id) }));
  };

  const addChemical = () => {
    const newId = Math.max(0, ...data.chemicals.map((c) => c.id)) + 1;
    const newName = t('sds.defaults.newChemicalName');
    setData((prev) => ({
      ...prev,
      chemicals: [
        ...prev.chemicals,
        {
          id: newId,
          name: newName,
          chemicalName: t('sds.defaults.newChemicalChemName'),
          unNumber: t('sds.defaults.newChemicalUn'),
          ghsClassification: t('sds.defaults.newChemicalGhs'),
          ghsSymbols: [],
          usage: t('sds.defaults.newChemicalUsage'),
          image: '',
          nfpa: { health: 0, fire: 0, reactivity: 0, special: '-' },
          risk: {
            material: newName,
            probability: t('sds.defaults.probabilityLow'),
            severity: t('sds.defaults.severityLow'),
            level: t('sds.defaults.riskLow'),
            class: t('sds.defaults.riskLow'),
          },
        },
      ],
    }));
  };

  return (
    <div
      id="sds-template"
      className="printable-area mx-auto bg-white shadow-2xl text-slate-900"
      style={{ width: '1100px', padding: '28px 32px', fontFamily: "'Cairo', 'Tajawal', sans-serif" }}
      dir={dir}
    >
      {/* Top row: decorative blue/green line on the LEFT, Authority text on the RIGHT (same line) */}
      <div className="flex items-start justify-between mb-2" dir={dir}>
        {/* Authority text — Arabic + English (English centered under Arabic) */}
        <div className="text-center pt-0.5 inline-block">
          <EditableText
            value={L(data.header.authorityName)}
            onChange={(v) => update('header.authorityName', v)}
            className="block text-slate-800 font-bold text-[15px] leading-[1.3] text-center"
          />
          <EditableText
            value={L(data.header.authorityNameEn)}
            onChange={(v) => update('header.authorityNameEn', v)}
            className="block text-slate-700 text-[11px] leading-[1.3] tracking-tight text-center"
          />
        </div>
        {/* Decorative bars on the LEFT side */}
        <div className="flex items-center gap-1" dir="ltr">
          <div className="w-12 h-1.5 bg-[#7bbf3f] rounded-sm" />
          <div className="w-32 h-1 bg-[#1a4d80] rounded-sm" />
        </div>
      </div>

      {/* Header — title perfectly centered; MODON logo on the far right with constrained height */}
      <div dir={dir} className="mb-4 relative min-h-[110px]">
        {/* MODON logo */}
        <div className={`absolute top-0 ${isEn ? 'left-0' : 'right-0'} w-[145px] h-[110px] flex items-center justify-center`}>
          <ModonLogo framed={false} maxHeight={110} />
        </div>

        {/* Facility / factory logo */}
        {data.facility?.logoDataUrl && (
          <div
            className={`absolute top-0 ${isEn ? 'right-0' : 'left-0'} w-[145px] h-[110px] flex items-center justify-center`}
          >
            <div className="w-[135px] h-[100px] flex items-center justify-center bg-white rounded-lg">
              <img
                src={data.facility.logoDataUrl}
                alt={data.facility?.name || 'شعار المصنع'}
                className="max-w-full max-h-full object-contain p-1"
              />
            </div>
          </div>
        )}

        <div className="w-full flex flex-col items-center justify-center min-h-[110px] text-center px-[155px]">
          <EditableText
            as="h1"
            value={L(data.header.title)}
            onChange={(v) => update('header.title', v)}
            className="block text-[#1a4d80] font-extrabold text-[26px] leading-[1.1] tracking-tight m-0 whitespace-nowrap"
          />
          <EditableText
            as="h2"
            value={L(data.header.subtitle)}
            onChange={(v) => update('header.subtitle', v)}
            className="block text-slate-700 font-bold text-[20px] mt-1 m-0 text-center"
          />
        </div>
      </div>

      {/* Facility Information Table - editable */}
      <div className="mb-3" dir={dir}>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="bg-[#1a4d80] text-white font-bold text-sm py-2 px-3 border border-[#143d68] w-1/3 text-center">
                {t('sds.facility.name')}
              </th>
              <th className="bg-[#1a4d80] text-white font-bold text-sm py-2 px-3 border border-[#143d68] w-1/3 text-center">
                {t('sds.facility.activity')}
              </th>
              <th className="bg-[#1a4d80] text-white font-bold text-sm py-2 px-3 border border-[#143d68] w-1/3 text-center">
                {t('sds.facility.location')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-blue-50/40">
              <td className="border border-slate-300 py-2 px-3 text-center text-sm font-semibold text-slate-800">
                <EditableText
                  value={data.facility?.name || ''}
                  onChange={(v) => update('facility.name', v)}
                  placeholder={t('sds.facility.namePh')}
                  className="block min-w-[80px]"
                />
              </td>
              <td className="border border-slate-300 py-2 px-3 text-center text-sm font-semibold text-slate-800">
                <EditableText
                  value={data.facility?.activity || ''}
                  onChange={(v) => update('facility.activity', v)}
                  placeholder={t('sds.facility.activityPh')}
                  className="block min-w-[80px]"
                />
              </td>
              <td className="border border-slate-300 py-2 px-3 text-center text-sm font-semibold text-slate-800">
                <EditableText
                  value={data.facility?.location || ''}
                  onChange={(v) => update('facility.location', v)}
                  placeholder={t('sds.facility.locationPh')}
                  className="block min-w-[80px]"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Section 1 - Chemicals registry */}
      <div className="mb-3">
        <div className="flex items-center justify-between bg-[#1a4d80] text-white px-3 py-1.5 rounded-t">
          <div className="flex items-center gap-2">
            <SectionBadge number="1" />
            <span className="font-bold text-base">{t('sds.registry.sectionTitle')}</span>
          </div>
          <button
            onClick={addChemical}
            className="no-print flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-2 py-1 rounded transition-colors"
            data-testid="sds-add-chemical-btn"
          >
            <Plus size={14} /> {t('sds.registry.addBtn')}
          </button>
        </div>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-slate-100 text-[#1a4d80]">
              <th className="border border-slate-300 p-1.5 font-bold w-12">{t('sds.registry.num')}</th>
              <th className="border border-slate-300 p-1.5 font-bold">{t('sds.registry.name')}</th>
              <th className="border border-slate-300 p-1.5 font-bold">{t('sds.registry.chemName')}</th>
              <th className="border border-slate-300 p-1.5 font-bold w-20">{t('sds.registry.un')}</th>
              <th className="border border-slate-300 p-1.5 font-bold">{t('sds.registry.ghs')}</th>
              <th className="border border-slate-300 p-1.5 font-bold w-32">{t('sds.registry.ghsSymbols')}</th>
              <th className="border border-slate-300 p-1.5 font-bold">{t('sds.registry.usage')}</th>
              <th className="border border-slate-300 p-1.5 font-bold w-20">{t('sds.registry.image')}</th>
              <th className="border-0 w-8 no-print" />
            </tr>
          </thead>
          <tbody>
            {data.chemicals.map((chem, idx) => (
              <tr key={chem.id} className="hover:bg-amber-50/30">
                <td className="border border-slate-300 p-1.5 text-center font-bold text-[#1a4d80]">{idx + 1}</td>
                <td className="border border-slate-300 p-1.5 font-bold text-[#1a4d80]">
                  <EditableText
                    value={chem.name}
                    onChange={(v) => updateChemical(chem.id, 'name', v)}
                    className="w-full"
                  />
                </td>
                <td className="border border-slate-300 p-1.5 text-center">
                  <EditableText
                    value={chem.chemicalName}
                    onChange={(v) => updateChemical(chem.id, 'chemicalName', v)}
                  />
                </td>
                <td className="border border-slate-300 p-1.5 text-center font-semibold">
                  <EditableText
                    value={chem.unNumber}
                    onChange={(v) => updateChemical(chem.id, 'unNumber', v)}
                  />
                </td>
                <td className="border border-slate-300 p-1.5 text-center">
                  <EditableText
                    value={chem.ghsClassification}
                    onChange={(v) => updateChemical(chem.id, 'ghsClassification', v)}
                  />
                </td>
                <td className="border border-slate-300 p-1.5 text-center">
                  <GHSIconRenderer symbols={chem.ghsSymbols} size={42} />
                </td>
                <td className="border border-slate-300 p-1.5 text-center">
                  <EditableText
                    value={chem.usage}
                    onChange={(v) => updateChemical(chem.id, 'usage', v)}
                  />
                </td>
                <td className="border border-slate-300 p-1 text-center">
                  {chem.image ? (
                    <img
                      src={chem.image}
                      alt={chem.name}
                      className="w-14 h-14 object-cover rounded mx-auto"
                    />
                  ) : (
                    <div className="w-14 h-14 bg-slate-100 rounded mx-auto flex items-center justify-center text-slate-400 text-xs">
                      {t('sds.registry.imagePh')}
                    </div>
                  )}
                </td>
                <td className="no-print align-middle pr-1">
                  <button
                    onClick={() => removeChemical(chem.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded"
                    title={t('sds.registry.deleteRow')}
                  >
                    <X size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-[11px] text-slate-600 mt-1.5 text-center">
          <EditableText value={L(data.notes)} onChange={(v) => update('notes', v)} />
        </div>
      </div>

      {/* Section 2 - NFPA + Legend (Legend on RIGHT, Diamonds on LEFT, equal heights) */}
      <div className="mb-4 grid grid-cols-12 gap-3" dir={dir}>
        {/* Legend on RIGHT (first in RTL) */}
        <div className="col-span-3 order-1 flex flex-col">
          <div className="flex items-center bg-slate-700 text-white px-3 h-9 rounded-t">
            <span className="font-bold text-sm">{L(data.nfpaLegend.title)}</span>
          </div>
          <ul className="border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white-b p-3 space-y-2 flex-1">
            {data.nfpaLegend.items.map((it, i) => (
              <li key={i} className="flex items-center gap-2 text-[12px]">
                <span
                  className="inline-block w-5 h-5 rounded-sm border border-slate-400 flex-shrink-0"
                  style={{ background: it.color }}
                />
                <span>{L(it.label)}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Diamonds on LEFT (second in RTL) - dynamic grid that auto-fits */}
        <div className="col-span-9 order-2 flex flex-col">
          <div className="flex items-center gap-2 bg-[#1a4d80] text-white px-3 h-9 rounded-t">
            <SectionBadge number="2" />
            <span className="font-bold text-base">{t('sds.nfpa.sectionTitle')}</span>
          </div>
          <div
            className="grid gap-2 px-2 py-3 border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white-b flex-1 items-center"
            style={{ gridTemplateColumns: `repeat(${Math.max(1, data.chemicals.length)}, minmax(0, 1fr))` }}
          >
            {data.chemicals.map((chem) => {
              // Auto-scale diamond width based on number of chemicals to keep them in one row
              const count = data.chemicals.length;
              const diamondWidth = count <= 4 ? 92 : count <= 5 ? 78 : count <= 6 ? 68 : count <= 8 ? 56 : 48;
              const fontSize = count <= 5 ? '12px' : '11px';
              return (
                <div key={chem.id} className="flex flex-col items-center gap-1">
                  <div
                    className="font-bold text-[#1a4d80] text-center min-h-[28px] flex items-center justify-center px-1 leading-tight"
                    style={{ fontSize }}
                  >
                    {chem.name}
                  </div>
                  <NFPADiamond
                    health={chem.nfpa.health}
                    fire={chem.nfpa.fire}
                    reactivity={chem.nfpa.reactivity}
                    special={chem.nfpa.special}
                    width={diamondWidth}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sections 3-5 row */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        {[
          { num: 3, key: 'safeStorage', icon: Warehouse },
          { num: 4, key: 'ppe', icon: HardHat },
          { num: 5, key: 'safeHandling', icon: ClipboardCheck },
        ].map(({ num, key, icon: Icon }) => (
          <div key={key} className="border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white p-2">
            <SectionHeader
              number={num}
              title={L(data[key].title)}
              onTitleChange={(v) => update(`${key}.title`, v)}
            />
            <div className="flex gap-2">
              <div className="flex-shrink-0 w-12 flex flex-col items-center justify-start pt-2">
                <div className="w-12 h-12 rounded-full bg-emerald-50 border-2 border-emerald-500 flex items-center justify-center">
                  <Icon className="text-emerald-600" size={24} />
                </div>
              </div>
              <ul className="flex-1 space-y-1">
                {data[key].items.map((item, i) => (
                  <Bullet key={i}>
                    <EditableText
                      value={L(item)}
                      onChange={(v) => {
                        const next = [...data[key].items];
                        next[i] = v;
                        update(`${key}.items`, next);
                      }}
                    />
                  </Bullet>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Sections 6 + First Aid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white p-2">
          <SectionHeader
            number="6"
            title={L(data.spillProcedures.title)}
            onTitleChange={(v) => update('spillProcedures.title', v)}
          />
          <div className="flex gap-2">
            <div className="flex-shrink-0 w-12 flex flex-col items-center justify-start pt-2">
              <div className="w-12 h-12 rounded-full bg-blue-50 border-2 border-[#1a4d80] flex items-center justify-center">
                <Droplets className="text-[#1a4d80]" size={24} />
              </div>
            </div>
            <ul className="flex-1 space-y-1">
              {data.spillProcedures.items.map((item, i) => (
                <Bullet key={i}>
                  <EditableText
                    value={L(item)}
                    onChange={(v) => {
                      const next = [...data.spillProcedures.items];
                      next[i] = v;
                      update('spillProcedures.items', next);
                    }}
                  />
                </Bullet>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white p-2">
          <div className="flex items-center gap-2 mb-2">
            <SectionBadge number="7" />
            <EditableText
              value={L(data.firstAid.title)}
              onChange={(v) => update('firstAid.title', v)}
              className="text-[#1a4d80] font-bold text-base"
            />
            <div className="flex-1 h-px bg-slate-300 ml-2" />
          </div>
          <div className="flex gap-2">
            <div className="flex-shrink-0 w-12 flex flex-col items-center justify-start pt-2">
              <div className="w-12 h-12 rounded-full bg-emerald-50 border-2 border-emerald-500 flex items-center justify-center">
                <Briefcase className="text-emerald-600" size={24} />
              </div>
            </div>
            <ul className="flex-1 space-y-1">
              {data.firstAid.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] leading-6">
                  <span className="text-emerald-700 font-bold whitespace-nowrap">
                    <EditableText
                      value={L(item.label)}
                      onChange={(v) => {
                        const next = [...data.firstAid.items];
                        next[i] = { ...next[i], label: v };
                        update('firstAid.items', next);
                      }}
                    />
                    :
                  </span>
                  <EditableText
                    value={L(item.text)}
                    onChange={(v) => {
                      const next = [...data.firstAid.items];
                      next[i] = { ...next[i], text: v };
                      update('firstAid.items', next);
                    }}
                    className="flex-1"
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Sections 8, 9, 10 */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        {/* 10 - Emergency Plan */}
        <div className="border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white p-2">
          <SectionHeader
            number="8"
            title={L(data.emergencyPlan.title)}
            onTitleChange={(v) => update('emergencyPlan.title', v)}
          />
          <div className="flex gap-2">
            <div className="flex-shrink-0 w-12 flex flex-col items-center justify-start pt-2">
              <div className="w-12 h-12 rounded-full bg-emerald-50 border-2 border-emerald-500 flex items-center justify-center">
                <Siren className="text-emerald-600" size={24} />
              </div>
            </div>
            <ul className="flex-1 space-y-1">
              {data.emergencyPlan.items.map((item, i) => (
                <Bullet key={i}>
                  <EditableText
                    value={L(item)}
                    onChange={(v) => {
                      const next = [...data.emergencyPlan.items];
                      next[i] = v;
                      update('emergencyPlan.items', next);
                    }}
                  />
                </Bullet>
              ))}
            </ul>
          </div>
        </div>

        {/* 9 - Risk Assessment */}
        <div className="border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white p-2">
          <SectionHeader
            number="9"
            title={t('sds.riskAssessment.sectionTitle')}
            onTitleChange={() => {}}
          />
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="bg-slate-100 text-[#1a4d80]">
                <th className="border border-slate-300 p-1">{t('sds.riskAssessment.material')}</th>
                <th className="border border-slate-300 p-1">{t('sds.riskAssessment.probability')}</th>
                <th className="border border-slate-300 p-1">{t('sds.riskAssessment.severity')}</th>
                <th className="border border-slate-300 p-1">{t('sds.riskAssessment.level')}</th>
                <th className="border border-slate-300 p-1">{t('sds.riskAssessment.class')}</th>
              </tr>
            </thead>
            <tbody>
              {data.chemicals.map((chem) => (
                <tr key={chem.id}>
                  <td className="border border-slate-300 p-1 text-center font-semibold">
                    <EditableText
                      value={chem.risk.material}
                      onChange={(v) => updateChemicalRisk(chem.id, 'material', v)}
                    />
                  </td>
                  <td className="border border-slate-300 p-1 text-center">
                    <EditableText
                      value={L(chem.risk.probability)}
                      onChange={(v) => updateChemicalRisk(chem.id, 'probability', v)}
                    />
                  </td>
                  <td className="border border-slate-300 p-1 text-center">
                    <EditableText
                      value={L(chem.risk.severity)}
                      onChange={(v) => updateChemicalRisk(chem.id, 'severity', v)}
                    />
                  </td>
                  <td className="border border-slate-300 p-1 text-center">
                    <EditableText
                      value={L(chem.risk.level)}
                      onChange={(v) => updateChemicalRisk(chem.id, 'level', v)}
                    />
                  </td>
                  <td className="border border-slate-300 p-0.5 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded font-bold ${risksColorClass(chem.risk.class)}`}>
                      <EditableText
                        value={L(chem.risk.class)}
                        onChange={(v) => updateChemicalRisk(chem.id, 'class', v)}
                      />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-center gap-3 mt-1.5 text-[11px]">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-600" />{t('sds.riskAssessment.high')}</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" />{t('sds.riskAssessment.medium')}</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-600" />{t('sds.riskAssessment.low')}</span>
          </div>
        </div>

        {/* 8 - Hypothetical scenario */}
        <div className="border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white p-2">
          <SectionHeader
            number="10"
            title={L(data.hypotheticalScenario.title)}
            onTitleChange={(v) => update('hypotheticalScenario.title', v)}
          />
          <div className="flex gap-2">
            <div className="flex-shrink-0 w-12 flex flex-col items-center justify-start pt-2">
              <div className="w-12 h-12 rounded-full bg-emerald-50 border-2 border-emerald-500 flex items-center justify-center">
                <UserCog className="text-emerald-600" size={24} />
              </div>
            </div>
            <div className="flex-1 text-[12px] leading-5">
              <EditableText
                value={L(data.hypotheticalScenario.incident)}
                onChange={(v) => update('hypotheticalScenario.incident', v)}
                className="block mb-1"
              />
              <div className="font-bold text-[#1a4d80]">
                <EditableText
                  value={L(data.hypotheticalScenario.actionsTitle)}
                  onChange={(v) => update('hypotheticalScenario.actionsTitle', v)}
                />
              </div>
              <ol className="list-decimal pr-4 space-y-0.5">
                {data.hypotheticalScenario.actions.map((a, i) => (
                  <li key={i}>
                    <EditableText
                      value={L(a)}
                      onChange={(v) => {
                        const next = [...data.hypotheticalScenario.actions];
                        next[i] = v;
                        update('hypotheticalScenario.actions', next);
                      }}
                    />
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Sections 11, 12, 13 */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        {/* 13 Emergency Numbers — Unified */}
        <div className="border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white p-2">
          <SectionHeader
            number="11"
            title={L(data.emergencyNumbers.title)}
            onTitleChange={(v) => update('emergencyNumbers.title', v)}
          />
          {(() => {
            // Backward compat: derive unifiedNumber & departments from legacy items[] if present
            const legacyItems = Array.isArray(data.emergencyNumbers.items) ? data.emergencyNumbers.items : null;
            const unifiedNumber = data.emergencyNumbers.unifiedNumber
              || (legacyItems && legacyItems[0]?.number)
              || '1920';
            const fallbackDepts = [t('sds.defaults.emergencyDept1'), t('sds.defaults.emergencyDept2'), t('sds.defaults.emergencyDept3')];
            const departments = Array.isArray(data.emergencyNumbers.departments)
              ? data.emergencyNumbers.departments
              : (legacyItems ? legacyItems.map((it) => it.label) : fallbackDepts);
            const setDept = (i, v) => {
              const next = [...departments];
              next[i] = v;
              update('emergencyNumbers.departments', next);
              if (legacyItems) update('emergencyNumbers.items', null);
            };
            return (
              <div className="px-2 py-1">
                <div className="bg-white border-2 border-red-300 rounded-lg p-4 flex flex-col items-center justify-center gap-3">
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex-shrink-0 bg-red-600 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-md">
                      <PhoneCall size={28}/>
                    </div>
                    <div className="text-sm text-red-700 font-bold tracking-wide text-center">{t('sds.emergency.unifiedTitle')}</div>
                    <div className="text-4xl font-extrabold text-red-700 leading-none" style={{ direction: 'ltr' }}>
                      <EditableText
                        value={unifiedNumber}
                        onChange={(v) => update('emergencyNumbers.unifiedNumber', v)}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 pt-1 max-w-full">
                    {departments.map((dep, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-300 rounded-full px-3 py-1 text-[12px] font-bold text-slate-800 whitespace-nowrap"
                      >
                        <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"/>
                        <EditableText value={L(dep)} onChange={(v) => setDept(i, v)} />
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* 12 Safety Culture */}
        <div className="border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white p-2">
          <SectionHeader
            number="12"
            title={L(data.safetyCulture.title)}
            onTitleChange={(v) => update('safetyCulture.title', v)}
          />
          <div className="flex gap-2">
            <div className="flex-shrink-0 w-12 flex flex-col items-center justify-start pt-2">
              <div className="w-12 h-12 rounded-full bg-emerald-50 border-2 border-emerald-500 flex items-center justify-center">
                <Users className="text-emerald-600" size={24} />
              </div>
            </div>
            <div className="flex-1 text-[12px] leading-5">
              <EditableText
                value={L(data.safetyCulture.text)}
                onChange={(v) => update('safetyCulture.text', v)}
                className="block"
              />
              <div className="text-emerald-700 font-extrabold text-base text-center mt-2">
                <EditableText
                  value={L(data.safetyCulture.motto)}
                  onChange={(v) => update('safetyCulture.motto', v)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 11 Training */}
        <div className="border-[1.5px] border-[#b8c8d9] rounded-lg shadow-[0_2px_6px_rgba(26,77,128,0.07)] bg-white p-2">
          <SectionHeader
            number="13"
            title={L(data.training.title)}
            onTitleChange={(v) => update('training.title', v)}
          />
          <div className="flex gap-2">
            <div className="flex-shrink-0 w-12 flex flex-col items-center justify-start pt-2">
              <div className="w-12 h-12 rounded-full bg-emerald-50 border-2 border-emerald-500 flex items-center justify-center">
                <ListChecks className="text-emerald-600" size={24} />
              </div>
            </div>
            <ul className="flex-1 space-y-1">
              {data.training.items.map((item, i) => (
                <Bullet key={i}>
                  <EditableText
                    value={L(item)}
                    onChange={(v) => {
                      const next = [...data.training.items];
                      next[i] = v;
                      update('training.items', next);
                    }}
                  />
                </Bullet>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Footer info row - RTL order: supervisor(right blue band) | nextReview | reviewNumber | issueNumber | date(left) */}
      <div className="grid grid-cols-12 gap-2 items-center pt-3 border-t-2 border-[#b8c8d9] text-[12px]" dir={dir}>
        {/* Right side - supervising authority (dark blue band) */}
        <div className="col-span-4">
          <div className="inline-flex items-center bg-[#1a4d80] text-white px-3 py-1.5 rounded font-bold text-[12px]">
            <EditableText
              value={L(data.footer.supervisor) || t('sds.defaults.footerSupervisor')}
              onChange={(v) => update('footer.supervisor', v)}
            />
          </div>
        </div>
        <div className="col-span-2 text-slate-700 text-center">
          <EditableText value={L(data.footer.nextReview)} onChange={(v) => update('footer.nextReview', v)} />
        </div>
        <div className="col-span-2 text-slate-700 text-center">
          <EditableText value={L(data.footer.reviewNumber)} onChange={(v) => update('footer.reviewNumber', v)} />
        </div>
        <div className="col-span-2 text-slate-700 text-center">
          <EditableText value={L(data.footer.issueNumber)} onChange={(v) => update('footer.issueNumber', v)} />
        </div>
        {/* Left side - date with calendar icon */}
        <div className="col-span-2 flex items-center gap-1.5 text-slate-700 justify-end">
          <Calendar size={14} className="text-[#1a4d80]" />
          <EditableText value={data.footer.date} onChange={(v) => update('footer.date', v)} />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2 text-[12px] text-slate-700" dir={dir}>
        <FileCheck2 size={16} className="text-emerald-600 flex-shrink-0" />
        <EditableText value={L(data.footer.notice)} onChange={(v) => update('footer.notice', v)} />
      </div>
    </div>
  );
}
